import { useState, useEffect, useCallback } from 'react';
import {
  Modal, Tabs, Tag, Avatar, Spin, Table, Button, DatePicker, message,
  Empty, Space, InputNumber, Input, Popconfirm,
} from 'antd';
import {
  UserOutlined, PhoneOutlined, IdcardOutlined, EnvironmentOutlined,
  DollarOutlined, TeamOutlined, CheckCircleOutlined, CalendarOutlined,
  TrophyOutlined, WalletOutlined, ShoppingOutlined, DeleteOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../../api/axios';
import '../Orders/OrderDetailModal.css';

const IMG = process.env.REACT_APP_URL;

const formatPrice = (p) => {
  if (!p && p !== 0) return '0';
  return Number(p).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

const STATUS_MAP = {
  active:   { color: 'green',  label: 'Faol' },
  on_leave: { color: 'orange', label: "Ta'tilda" },
  fired:    { color: 'red',    label: "Ishdan bo'shgan" },
};

const ORDER_STATUS = {
  pending: { c: 'orange', l: 'Kutilmoqda' },
  confirmed: { c: 'blue', l: 'Tasdiqlangan' },
  in_progress: { c: 'geekblue', l: 'Jarayonda' },
  completed: { c: 'green', l: 'Bajarildi' },
  cancelled: { c: 'red', l: 'Bekor' },
  rejected: { c: 'volcano', l: 'Rad etilgan' },
};

export default function EmployeeProfileModal({ open, employeeId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState(dayjs());
  const [payroll, setPayroll] = useState(null);
  const [payrollLoading, setPayrollLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('info');

  const fetchProfile = useCallback(() => {
    if (!employeeId) return;
    setLoading(true);
    api.get(`/employees/${employeeId}/profile`)
      .then(res => setData(res.data?.data || null))
      .catch(() => message.error('Xodim profili yuklanmadi'))
      .finally(() => setLoading(false));
  }, [employeeId]);

  const fetchPayroll = useCallback((p) => {
    if (!employeeId) return;
    setPayrollLoading(true);
    api.get(`/employees/${employeeId}/payroll?month=${p.month() + 1}&year=${p.year()}`)
      .then(res => setPayroll(res.data?.data || null))
      .catch(() => message.error('Maosh hisobi yuklanmadi'))
      .finally(() => setPayrollLoading(false));
  }, [employeeId]);

  useEffect(() => {
    if (open && employeeId) {
      const p = dayjs();
      setPeriod(p);
      setActiveTab('info');
      fetchProfile();
      fetchPayroll(p);
    }
  }, [open, employeeId, fetchProfile, fetchPayroll]);

  const [payAmount, setPayAmount] = useState(null);
  const [payNote, setPayNote] = useState('');

  const addSalaryPayment = () => {
    if (!payAmount || payAmount <= 0) { message.warning('Summa kiriting'); return; }
    api.post(`/employees/${employeeId}/salary-payment`, {
      month: period.month() + 1, year: period.year(), amount: payAmount, note: payNote || null,
    }).then(() => {
      setPayAmount(null); setPayNote('');
      fetchPayroll(period); fetchProfile();
      message.success('To\'lov yozildi');
    }).catch(err => message.error(err.response?.data?.message || 'Xatolik'));
  };

  const deleteSalaryPayment = (payId) => {
    api.delete(`/employees/salary-payment/${payId}`)
      .then(() => { fetchPayroll(period); fetchProfile(); message.success('To\'lov o\'chirildi'); })
      .catch(() => message.error('Xatolik'));
  };

  const emp = data?.employee;
  const kpi = data?.kpi;
  const ws = emp ? (STATUS_MAP[emp.work_status] || { color: 'default', label: emp.work_status || '—' }) : {};

  const historyColumns = [
    { title: '#', dataIndex: 'order_id', width: 60, render: v => <b>#{v}</b> },
    { title: 'Xizmat', dataIndex: 'service_name', render: v => v || '—' },
    { title: 'Sana', key: 'date', render: (_, r) => `${r.day}/${r.month}/${r.year}` },
    {
      title: 'Buyurtma', dataIndex: 'order_status', width: 120,
      render: v => { const s = ORDER_STATUS[v] || { c: 'default', l: v }; return <Tag color={s.c}>{s.l}</Tag>; },
    },
    { title: 'Haq', dataIndex: 'pay_amount', align: 'right', render: v => <b>{formatPrice(v)}</b> },
  ];

  // ─── Hero ───
  const renderHero = () => (
    <div className="odm-hero">
      <div className="odm-hero-top">
        <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
          <Avatar size={56} icon={<UserOutlined />} src={emp.avatar ? IMG + emp.avatar : undefined}
            style={{ background: 'rgba(255,255,255,0.25)', flexShrink: 0 }} />
          <div>
            <div className="odm-hero-id">{emp.fullname || 'Xodim'}</div>
            <div className="odm-hero-sub">
              {emp.position_name && <><TrophyOutlined /> {emp.position_name}<span className="odm-dot">•</span></>}
              <Tag color={ws.color} style={{ margin: 0 }}>{ws.label}</Tag>
            </div>
          </div>
        </div>
      </div>
      <div className="odm-hero-stats" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
        <div className="odm-stat"><span className="odm-stat-label">Buyurtmalar</span><span className="odm-stat-val">{kpi.total_orders}</span></div>
        <div className="odm-stat"><span className="odm-stat-label">Bajarildi</span><span className="odm-stat-val">{kpi.completion_rate}%</span></div>
        <div className="odm-stat"><span className="odm-stat-label">Jami daromad</span><span className="odm-stat-val odm-green">{formatPrice(kpi.total_earned)}</span></div>
        <div className="odm-stat"><span className="odm-stat-label">Shu oy</span><span className="odm-stat-val odm-amber">{formatPrice(kpi.this_month_earned)}</span></div>
      </div>
    </div>
  );

  // ─── Tab: Ma'lumot (shaxsiy kartochka) ───
  const renderInfo = () => (
    <div className="odm-section-wrap">
      <div className="odm-card">
        <div className="odm-card-head"><span><IdcardOutlined /> Shaxsiy ma'lumotlar</span></div>
        <div className="odm-info-grid">
          <div><span className="odm-k"><PhoneOutlined /> Telefon</span>
            <span className="odm-v">{emp.phone ? <a href={`tel:${emp.phone}`}>{emp.phone}</a> : '—'}</span></div>
          <div><span className="odm-k"><IdcardOutlined /> JSHSHIR</span><span className="odm-v">{emp.jshshir || '—'}</span></div>
          <div><span className="odm-k"><EnvironmentOutlined /> Manzil</span><span className="odm-v">{[emp.region, emp.address].filter(Boolean).join(', ') || '—'}</span></div>
          <div><span className="odm-k"><TrophyOutlined /> Lavozim</span><span className="odm-v">{emp.position_name || '—'}</span></div>
          <div><span className="odm-k"><CalendarOutlined /> Ro'yxatga olingan</span><span className="odm-v">{emp.created ? String(emp.created).slice(0, 10) : '—'}</span></div>
          {emp.fire && <div><span className="odm-k">Ishdan bo'shagan</span><span className="odm-v">{String(emp.fire).slice(0, 10)}</span></div>}
        </div>
      </div>

      <div className="odm-card">
        <div className="odm-card-head"><span><DollarOutlined /> Maosh sozlamasi</span></div>
        <div className="odm-info-grid">
          <div><span className="odm-k">Asosiy maosh</span><span className="odm-v" style={{ color: '#22c55e', fontWeight: 700 }}>{formatPrice(emp.salary)} so'm</span></div>
          <div><span className="odm-k">Maosh turi</span><span className="odm-v">{emp.salary_type || 'oylik'}</span></div>
        </div>
      </div>

      {/* KPI batafsil */}
      <div className="odm-fin-summary">
        <div className="odm-fin-item"><span><ShoppingOutlined /> Jami biriktirilgan buyurtmalar</span><b>{kpi.total_orders} ta</b></div>
        <div className="odm-fin-item"><span><CheckCircleOutlined /> Bajarilgan buyurtmalar</span><b>{kpi.completed_orders} ta ({kpi.completion_rate}%)</b></div>
        <div className="odm-fin-item"><span><TeamOutlined /> Ishlagan xizmatlar soni</span><b>{kpi.services_count} ta</b></div>
        <div className="odm-fin-item"><span><DollarOutlined /> O'rtacha buyurtma haqi</span><b>{formatPrice(kpi.avg_pay)} so'm</b></div>
        <div className="odm-fin-item odm-fin-profit"><span className="odm-green">Jami to'langan maosh</span><b className="odm-green">{formatPrice(kpi.paid_earned)} so'm</b></div>
      </div>
    </div>
  );

  // ─── Tab: Ish tarixi ───
  const renderHistory = () => (
    <div className="odm-section-wrap">
      <div className="odm-card" style={{ padding: 0, overflow: 'hidden' }}>
        <Table
          size="small"
          rowKey="emp_salary_id"
          columns={historyColumns}
          dataSource={data.history}
          pagination={false}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Hali buyurtmalarda ishlamagan" /> }}
        />
      </div>
    </div>
  );

  // ─── Tab: Maosh hisobi ───
  const renderPayroll = () => (
    <div className="odm-section-wrap">
      <div className="odm-card">
        <div className="odm-card-head">
          <span><WalletOutlined /> Oylik maosh hisobi</span>
          <DatePicker picker="month" value={period} allowClear={false} format="MMMM YYYY"
            onChange={(p) => { setPeriod(p); fetchPayroll(p); }} />
        </div>
        {payrollLoading ? <div style={{ textAlign: 'center', padding: 20 }}><Spin /></div> : payroll && (
          <>
            <div className="odm-fin-summary" style={{ marginBottom: 14 }}>
              <div className="odm-fin-item"><span>Asosiy maosh ({payroll.salary_type})</span><b>{formatPrice(payroll.base_salary)} so'm</b></div>
              <div className="odm-fin-item"><span>Buyurtmalardan daromad</span><b className="odm-green">{formatPrice(payroll.order_earnings)} so'm</b></div>
              <div className="odm-fin-item"><span>JAMI hisoblangan</span><b>{formatPrice(payroll.total)} so'm</b></div>
              <div className="odm-fin-item"><span className="odm-green">To'langan</span><b className="odm-green">{formatPrice(payroll.paid)} so'm</b></div>
              <div className="odm-fin-item odm-fin-profit"><span>Qoldiq</span><b className="odm-amber">{formatPrice(payroll.remaining)} so'm</b></div>
            </div>

            {/* To'lov qo'shish */}
            <div className="odm-add-row" style={{ marginBottom: 12 }}>
              <InputNumber placeholder="To'lov summasi" value={payAmount} onChange={setPayAmount} min={1} style={{ flex: 1 }}
                formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} parser={v => v.replace(/\s/g, '')} />
              <Input placeholder="Izoh" value={payNote} onChange={e => setPayNote(e.target.value)} style={{ width: 140 }} />
              <Button type="primary" icon={<DollarOutlined />} onClick={addSalaryPayment}
                style={{ background: '#22c55e', borderColor: '#22c55e' }}>To'lash</Button>
            </div>

            {/* To'lovlar tarixi */}
            {(payroll.payments || []).length > 0 && (
              <div className="odm-card" style={{ marginBottom: 12 }}>
                <div className="odm-card-head"><span>To'lovlar tarixi</span></div>
                {payroll.payments.map(p => (
                  <div key={p.id} className="odm-row">
                    <div><span style={{ fontSize: 13 }}>{p.note || 'Maosh to\'lovi'}</span>
                      <span className="odm-muted" style={{ fontSize: 11, marginLeft: 8 }}>{p.created ? String(p.created).slice(0, 10) : ''}</span></div>
                    <Space size={8}>
                      <span className="odm-green">+{formatPrice(p.amount)} so'm</span>
                      <Popconfirm title="O'chirish?" onConfirm={() => deleteSalaryPayment(p.id)} okText="Ha" cancelText="Yo'q">
                        <Button type="text" danger size="small" icon={<DeleteOutlined />} />
                      </Popconfirm>
                    </Space>
                  </div>
                ))}
              </div>
            )}

            <div className="odm-card-head" style={{ marginBottom: 8 }}><span><ShoppingOutlined /> Shu oydagi buyurtmalar</span></div>
            <Table
              size="small"
              rowKey="emp_salary_id"
              columns={historyColumns}
              dataSource={payroll.items}
              pagination={false}
              locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Bu oyda buyurtma yo'q" /> }}
            />
          </>
        )}
      </div>
    </div>
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={760}
      title={null}
      closeIcon={null}
      destroyOnHidden
      className="odm-modal"
      styles={{ body: { padding: 0 } }}
    >
      {!data ? (
        <div style={{ padding: 60, textAlign: 'center' }}><Spin size="large" /></div>
      ) : (
        <>
          {renderHero()}
          <div className="odm-actions">
            <Space size={8}>
              {emp.phone && <Button size="small" icon={<PhoneOutlined />} href={`tel:${emp.phone}`}>Qo'ng'iroq</Button>}
              {loading && <Spin size="small" />}
            </Space>
            <Button size="small" type="text" onClick={onClose}>✕</Button>
          </div>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            className="odm-tabs"
            items={[
              { key: 'info', label: <span><IdcardOutlined /> Ma'lumot</span>, children: renderInfo() },
              { key: 'history', label: <span><ShoppingOutlined /> Ish tarixi</span>, children: renderHistory() },
              { key: 'payroll', label: <span><WalletOutlined /> Maosh</span>, children: renderPayroll() },
            ]}
          />
        </>
      )}
    </Modal>
  );
}
